import React, { FC, RefObject } from 'react';
import DOMPurify from 'dompurify';
import { Accordion, AccordionRef } from '@components/atoms/Accordion';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import styles from './IconAccordion.module.css';

type IconAccordionSubComponents = {
    IndentedContent: typeof IndentedContent;
};

export interface IconAccordionProps {
    accordionRef?: RefObject<AccordionRef>;
    id: string;
    items: {
        id: string;
        title: string;
        description: string;
        icon: string;
        content: string | JSX.Element | JSX.Element[];
    }[];
}

export const IconAccordion: FC<IconAccordionProps> & IconAccordionSubComponents = ({
    accordionRef,
    id,
    items,
}: IconAccordionProps) => {
    return (
        <Accordion
            ref={accordionRef}
            id={id}
            items={items.map((item) => {
                return {
                    id: item.id,
                    buttonContent: (
                        <div className="d-flex gap-4">
                            <i className={`${styles.icon} ${item.icon}`}></i>
                            <div className="d-flex flex-column">
                                <Typography classes="mb-1" tagVariant={TypographyTagVariants.h2} fontSize="16" fontWeight="900">
                                    {item.title}
                                </Typography>
                                <Typography fontSize="14" tagVariant={TypographyTagVariants.span}>
                                    <div
                                        className={`${styles['description']} text-muted`}
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description) }}></div>
                                </Typography>
                            </div>
                        </div>
                    ),
                    bodyContent: item.content,
                };
            })}
            customClasses={{
                accordion: styles['icon-accordion'],
                accordionItem: styles['custom-accordion-item'],
                accordionButton: `${styles['custom-accordion-button']} py-4 pe-4 ps-0`,
                accordionBody: 'pt-0 pb-4 px-4 gap-4',
            }}
        />
    );
};

interface IndentedContentProps {
    children: string | JSX.Element | JSX.Element[];
}

const IndentedContent: FC<IndentedContentProps> = ({ children }) => {
    return <div className={styles['indent']}>{children}</div>;
};

IconAccordion.IndentedContent = IndentedContent;
